import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '@/lib/api';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowLeft, ArrowRight, Package, Loader2, Upload, Handshake } from 'lucide-react';

const SITE = 'https://www.destockpro.be';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absImg = (src) => (!src ? null : (src.startsWith('http') ? src : `${BACKEND}${src}`));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishedSlugs, setPublishedSlugs] = useState(null);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
    api.get('/blog/slugs')
      .then((r) => setPublishedSlugs(new Set(r.data || [])))
      .catch(() => setPublishedSlugs(new Set()));
  }, [slug]);

  // Custom link renderer: internal links -> react-router; links to unpublished
  // blog articles are rendered as plain text to avoid empty pages.
  const markdownComponents = useMemo(() => ({
    a: ({ href, children }) => {
      if (!href) return <span>{children}</span>;
      if (/^https?:\/\//i.test(href) && !href.includes('destockpro.be')) {
        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
      }
      let path = href.replace(/^https?:\/\/[^/]+/i, '');
      if (!path.startsWith('/')) path = '/' + path;
      const blogMatch = path.match(/^\/blog\/([^/?#]+)/);
      if (blogMatch && publishedSlugs && !publishedSlugs.has(blogMatch[1])) {
        return <span>{children}</span>;
      }
      return <Link to={path}>{children}</Link>;
    },
  }), [publishedSlugs]);

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="blog-article-loading">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="blog-article-not-found">
        <p className="text-lg font-semibold text-neutral-700 mb-2">Article introuvable</p>
        <p className="text-neutral-500 mb-6">Cet article n'existe pas ou n'est plus disponible.</p>
        <Button variant="outline" asChild>
          <Link to="/blog"><ArrowLeft className="w-4 h-4 mr-2" /> Retour au blog</Link>
        </Button>
      </div>
    );
  }

  const coverDisplay = absImg(article.cover_image);
  const coverCanonical = article.cover_image
    ? (article.cover_image.startsWith('http') ? article.cover_image : `${SITE}${article.cover_image}`)
    : null;
  const url = `${SITE}/blog/${article.slug}`;
  const seoTitle = (article.meta_title || article.title || '').replace(/\s*\|\s*DestockPro\s*$/i, '');
  const related = article.related || [];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.meta_description || article.excerpt,
    ...(coverCanonical ? { image: coverCanonical } : {}),
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { '@type': 'Organization', name: 'DestockPro', url: SITE },
    publisher: {
      '@type': 'Organization',
      name: 'DestockPro',
      logo: { '@type': 'ImageObject', url: `${SITE}/icon-512.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };

  return (
    <div data-testid="blog-article-page">
      <SEO
        title={seoTitle}
        description={article.meta_description || article.excerpt}
        path={`/blog/${article.slug}`}
        image={coverCanonical}
        type="article"
        schema={schema}
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#1F3F72] transition-colors mb-8" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" /> Retour au blog
        </Link>

        <p className="flex items-center gap-1.5 text-sm text-neutral-400 mb-3">
          <Calendar className="w-4 h-4" /> Publié le {fmtDate(article.published_at)}
        </p>

        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 leading-tight mb-8" data-testid="blog-article-title">
          {article.title}
        </h1>

        {coverDisplay && (
          <img src={coverDisplay} alt={article.title} className="w-full rounded-xl mb-10 aspect-video object-cover" />
        )}

        <div className="blog-content" data-testid="blog-article-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {article.content}
          </ReactMarkdown>
        </div>

        {/* À lire aussi */}
        {related.length > 0 && (
          <section className="mt-14 pt-8 border-t border-neutral-100" data-testid="blog-related">
            <h2 className="font-heading text-xl font-bold text-neutral-900 mb-5">À lire aussi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => {
                const rc = absImg(r.cover_image);
                return (
                  <Link
                    key={r.id}
                    to={`/blog/${r.slug}`}
                    className="group flex flex-col rounded-xl border border-neutral-100 overflow-hidden bg-white hover:shadow-md transition-all"
                    data-testid={`blog-related-${r.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#1F3F72] flex items-center justify-center">
                      {rc ? (
                        <img src={rc} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <Package className="w-8 h-8 text-white/25" />
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-neutral-900 leading-snug line-clamp-3 group-hover:text-[#1F3F72] transition-colors">{r.title}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Author box */}
        <div className="mt-12 flex items-start gap-4 rounded-xl border border-neutral-100 bg-neutral-50 p-5" data-testid="blog-author-box">
          <div className="w-11 h-11 rounded-lg bg-[#1F3F72] flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm text-neutral-600 leading-relaxed">
            Rédigé par l'équipe <span className="font-semibold text-neutral-900">DestockPro</span>, la place de marché du déstockage entre professionnels.
          </p>
        </div>

        {/* CTA box */}
        <div className="mt-6 rounded-xl bg-[#1F3F72] p-6 sm:p-8" data-testid="blog-cta-box">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-2">
            Prêt à transformer votre stock dormant en trésorerie ?
          </h2>
          <p className="text-sm sm:text-base text-white/80 mb-6 max-w-xl">
            Publiez gratuitement vos annonces, sans commission sur les ventes. Ou confiez-nous tout : nous trions, photographions et mettons en ligne pour vous.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-[#F25C05] hover:bg-[#d94f04] text-white font-semibold">
              <Link to="/inscription" data-testid="blog-cta-register"><Upload className="w-4 h-4 mr-2" /> Créer un compte gratuit</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white font-semibold">
              <Link to="/service-publication" data-testid="blog-cta-service"><Handshake className="w-4 h-4 mr-2" /> Le service clés en main <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
}
